import type { PaletteColor } from "@brexhq/metal-foundation";
import { useSetAtom } from "jotai";
import qs from "qs";
import React, { FormEvent, useState, useEffect } from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import {
  getModuleThemeColors,
  ModuleLayout,
} from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import useDirtyState from "@brexhq/next/src/hooks/useDirtyState";
import useLocation from "@brexhq/next/src/hooks/useLocation";
import useSiteContext from "@brexhq/next/src/hooks/useSiteContext";
import { userEmailAtom } from "@brexhq/next/src/state/user";
import {
  FieldDirtiedEvent,
  FormSubmittedEvent,
} from "@brexhq/next/src/telemetry/analyticsEvents";
import isValidEmail from "@brexhq/next/src/utils/isValidEmail";
import type { SanityInlineSignupFormProps } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems";
import type { ModuleLayoutProps } from "@brexhq/shared/types/modules/ModuleLayout";

import { GridWidth } from "../../shared/Grid";

import { DesktopForm, InlineSSUFormProps, MobileForm } from "./components/Form";
import { FormError } from "./components/FormError";
import { buildUrlWithParams, insertDashes, removeDashes } from "./utils";

export interface DeprecatedInlineSignupFormProps {
  data: {
    moduleLayout: ModuleLayoutProps;
    capture: "EMAIL" | "PREF_TOKEN";
    btnLabel: string;
    anchor?: string;
    alignment: "left" | "center";
  };
}

/**
 * Lives in Block module.
 * Captures user email or pref token.
 */

/**
 * @deprecated
 * Use {@link: InlineSignupForm} instead
 */
export const DeprecatedInlineSignupForm: React.FC<
  DeprecatedInlineSignupFormProps
> = ({ data: { btnLabel, capture, moduleLayout, alignment, anchor } }) => {
  const { theme } = moduleLayout;
  const {
    standard: { textPrimary },
  } = getModuleThemeColors(theme);

  const { trackEvent } = useAnalytics();
  const { search } = useLocation();
  const { navigators } = useSiteContext();
  const rsvpTokenLength = 14 + 2; //14 is the token 2 is the dashes
  const [formInput, setFormInput] = useState<string>("");
  const [hasError, setHasError] = useState(false);
  const setGlobalUserEmail = useSetAtom(userEmailAtom);

  const formName = `Inline SSU - ${capture}`;
  const fieldName = capture;
  const { updateDirtyState } = useDirtyState(formInput, () => {
    trackEvent(new FieldDirtiedEvent(formName, fieldName));
  });

  const isEmailField = capture === "EMAIL";

  const setPrefValue = (dashedValue: string, isDeletingDash = false) => {
    // We need to remove Dashes before we insert them in the correct spot

    const cleanedVal = removeDashes(dashedValue);
    setFormInput(insertDashes(cleanedVal, isDeletingDash));
  };

  useEffect(() => {
    const rsvpToken = qs.parse(search, {
      ignoreQueryPrefix: true,
    }).rsvp as string;
    // we are only setting this from a qr code so it will always only be a single value

    if (!isEmailField && rsvpToken) {
      setPrefValue(rsvpToken);
    }
  }, [search, isEmailField]);

  const handleFormInput = (evt: React.FormEvent<HTMLInputElement>): void => {
    const inputVal = evt.currentTarget.value;
    updateDirtyState(inputVal);
    const isLastCharDash = formInput.substr(-1) === "-";
    const isDeletingDash =
      inputVal.length === formInput.length - 1 && isLastCharDash;

    if (isEmailField) {
      setFormInput(inputVal);
    } else {
      if (inputVal.length <= rsvpTokenLength) {
        setPrefValue(inputVal, isDeletingDash);
      }
    }
  };

  const onFormSubmit = (evt: FormEvent): void => {
    evt.preventDefault();
    trackEvent(new FormSubmittedEvent(formName));

    if (formInput === "") {
      navigators.function("/signup");
    }
    const isValid = isEmailField
      ? isValidEmail(formInput)
      : formInput.length === rsvpTokenLength;

    if (!isValid) return setHasError(true);

    if (isEmailField) {
      navigators.function("/signup/");
      setGlobalUserEmail(formInput);
    } else {
      navigators.function(buildUrlWithParams({ formInput }));
    }
  };

  const formProps: InlineSSUFormProps = {
    type: capture,
    id: anchor,
    value: formInput,
    handleFormInput,
    handleFormSubmit: onFormSubmit,
    hasError,
    submitButtonLabel: btnLabel,
  };

  return (
    <ModuleLayout {...moduleLayout}>
      <GridWidth>
        <Container
          display="flex"
          direction="column"
          alignItems={alignment === "center" ? " center" : undefined}
          justifyContent="flex-start"
        >
          <DesktopForm {...formProps} />
          <MobileForm {...formProps} />

          {hasError && (
            <FormError textColor={textPrimary}>
              {isEmailField
                ? "Please enter a valid email"
                : "RSVP code must be 16 characters long (including dashes)"}
            </FormError>
          )}
        </Container>
      </GridWidth>
    </ModuleLayout>
  );
};

export interface Props
  extends Omit<SanityInlineSignupFormProps, "_key" | "_type"> {
  id: string;
  textColor: PaletteColor;

  // Used for storybook only.
  forceError?: boolean;
}

export const InlineSignupForm: React.FC<Props> = ({
  id,
  btnLabel,
  capture,
  textColor,
  forceError = false,
}) => {
  const { trackEvent } = useAnalytics();
  const { search } = useLocation();
  const { navigators } = useSiteContext();
  const rsvpTokenLength = 14 + 2; //14 is the token 2 is the dashes
  const [formInput, setFormInput] = useState<string>("");
  const [hasError, setHasError] = useState(false);
  const setGlobalUserEmail = useSetAtom(userEmailAtom);

  const formName = `Inline SSU - ${capture}`;
  const fieldName = capture;
  const { updateDirtyState } = useDirtyState(formInput, () => {
    trackEvent(new FieldDirtiedEvent(formName, fieldName));
  });

  const isEmailField = capture === "EMAIL";

  const setPrefValue = (dashedValue: string, isDeletingDash = false) => {
    // We need to remove Dashes before we insert them in the correct spot

    const cleanedVal = removeDashes(dashedValue);
    setFormInput(insertDashes(cleanedVal, isDeletingDash));
  };

  useEffect(() => {
    const rsvpToken = qs.parse(search, {
      ignoreQueryPrefix: true,
    }).rsvp as string;
    // we are only setting this from a qr code so it will always only be a single value

    if (!isEmailField && rsvpToken) {
      setPrefValue(rsvpToken);
    }
  }, [search, isEmailField]);

  const handleFormInput = (evt: React.FormEvent<HTMLInputElement>): void => {
    const inputVal = evt.currentTarget.value;
    updateDirtyState(inputVal);
    const isLastCharDash = formInput.substr(-1) === "-";
    const isDeletingDash =
      inputVal.length === formInput.length - 1 && isLastCharDash;

    if (isEmailField) {
      setFormInput(inputVal);
    } else {
      if (inputVal.length <= rsvpTokenLength) {
        setPrefValue(inputVal, isDeletingDash);
      }
    }
  };

  const onFormSubmit = (evt: FormEvent): void => {
    evt.preventDefault();
    trackEvent(new FormSubmittedEvent(formName));

    if (formInput === "") {
      navigators.function("/signup");
    }
    const isValid = isEmailField
      ? isValidEmail(formInput)
      : formInput.length === rsvpTokenLength;

    if (!isValid) return setHasError(true);

    if (isEmailField) {
      navigators.function("/signup/");
      setGlobalUserEmail(formInput);
    } else {
      navigators.function(buildUrlWithParams({ formInput }));
    }
  };

  const formProps: InlineSSUFormProps = {
    type: capture,
    id,
    value: formInput,
    handleFormInput,
    handleFormSubmit: onFormSubmit,
    hasError,
    submitButtonLabel: btnLabel,
  };

  return (
    <>
      <DesktopForm {...formProps} />
      <MobileForm {...formProps} />
      {(hasError || forceError) && (
        <FormError textColor={textColor}>
          {isEmailField
            ? "Please enter a valid email"
            : "RSVP code must be 16 characters long (including dashes)"}
        </FormError>
      )}
    </>
  );
};
